import type { api } from "@healthos/schemas";
import { MEDICATIONS } from "@healthos/shared";

// Shared GLP-1 tracking presentation helpers. Pure functions only, used by the
// full Results tab and the compact ambient dosage widget so a given dose reads
// as the same colour and a weight converts to the same display value across
// every surface. See ADR-0016 / ADR-0017.

const KG_PER_LB = 0.45359237;
const LB_PER_KG = 1 / KG_PER_LB;
export const DAY_MS = 24 * 60 * 60 * 1000;

export const LINE_TEAL = "#0f766e";

export {
  CADENCE_OPTIONS,
  MEDICATION_KEYS,
  MEDICATIONS,
  type MedicationSpec,
  medicationCadence,
  medicationDoses,
  medicationLabel,
} from "@healthos/shared";

// Ordered titration palette: a dose is coloured by its rank within its
// medication's ladder (1st step gray, then purple, teal, pink, blue, red), so
// every medication reads as a clean low-to-high progression even though the
// absolute mg differ. Tirzepatide keeps its familiar colours since its ladder
// order matches this palette.
const DOSE_PALETTE = ["#6b7280", "#8b5cf6", "#14b8a6", "#ec4899", "#3b82f6", "#ef4444"];

// Legacy nearest-mg buckets, kept as the fallback when no medication is known.
const DOSE_COLORS: Array<{ mg: number; color: string }> = [
  { mg: 2.5, color: "#6b7280" },
  { mg: 5, color: "#8b5cf6" },
  { mg: 7.5, color: "#14b8a6" },
  { mg: 10, color: "#ec4899" },
  { mg: 12.5, color: "#3b82f6" },
  { mg: 15, color: "#ef4444" },
];

export function doseColor(mg: number, medication?: string | null): string {
  if (medication) {
    const ladder = MEDICATIONS[medication.toLowerCase()]?.doses;
    if (ladder) {
      const i = ladder.indexOf(mg);
      const c = i >= 0 ? DOSE_PALETTE[Math.min(i, DOSE_PALETTE.length - 1)] : undefined;
      if (c) return c;
    }
  }
  return DOSE_COLORS.reduce((best, d) => (Math.abs(d.mg - mg) < Math.abs(best.mg - mg) ? d : best))
    .color;
}

export function toDisplay(kg: number, unit: api.WeightUnit): number {
  return unit === "lb" ? Math.round(kg * LB_PER_KG * 10) / 10 : Math.round(kg * 10) / 10;
}

export function displayToKg(value: number, unit: api.WeightUnit): number {
  return unit === "lb" ? value * KG_PER_LB : value;
}

export function titleCase(s: string): string {
  return s.length === 0 ? s : `${s[0]?.toUpperCase()}${s.slice(1)}`;
}

// Build the stops for a horizontal stroke gradient so the weight line is
// coloured by whichever dose was active at each point in time, switching hard
// at each titration change (mirrors the Shotsy "track by dose" line). Returns
// [] when there are no doses, so the caller falls back to a solid teal line.
export function doseGradientStops(
  points: ReadonlyArray<{ t: number }>,
  doses: ReadonlyArray<api.DoseEntry>,
): Array<{ offset: number; color: string; key: string }> {
  if (points.length === 0 || doses.length === 0) return [];
  const minT = points[0]?.t ?? 0;
  const maxT = points[points.length - 1]?.t ?? minT;
  const span = maxT - minT || 1;
  const sorted = doses.toSorted(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );
  const colorAt = (t: number): string => {
    const first = sorted[0];
    let c = first ? doseColor(first.doseAmount, first.medication) : LINE_TEAL;
    for (const d of sorted) {
      if (new Date(d.occurredAt).getTime() <= t) c = doseColor(d.doseAmount, d.medication);
      else break;
    }
    return c;
  };
  const stops: Array<{ offset: number; color: string; key: string }> = [];
  let seq = 0;
  const push = (offset: number, color: string) => {
    seq += 1;
    stops.push({ offset, color, key: `stop-${seq}` });
  };
  let current = colorAt(minT);
  push(0, current);
  for (const d of sorted) {
    const dt = new Date(d.occurredAt).getTime();
    if (dt <= minT || dt >= maxT) continue;
    const c = doseColor(d.doseAmount, d.medication);
    if (c !== current) {
      const offset = (dt - minT) / span;
      push(offset, current); // close the previous colour
      push(offset, c); // hard switch to the new colour
      current = c;
    }
  }
  push(1, current);
  return stops;
}
